/* import __COLOCATED_TEMPLATE__ from './content-review.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import type Model from '@ember-data/model';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type Block } from '@intercom/interblocks.ts';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'ember-intl/services/intl';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';
import { DRAWER_EDITOR_SHOW_SIDE_PANEL } from 'embercom/lib/knowledge-hub/constants';
import RouteRegexes from 'embercom/lib/route-regexes';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import { type KnowledgeHubEditorConfig } from 'embercom/objects/knowledge-hub/knowledge-hub-editor-config';
import { type LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type ContentImportService from 'embercom/services/content-import-service';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type SuggestionService from 'embercom/services/suggestion-service';
import type { ReviewableContent } from 'embercom/services/suggestion-service';
import storage from 'embercom/vendor/intercom/storage';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import ENV from 'embercom/config/environment';
import { timeout } from 'ember-concurrency';

import type { EntityIdentifierTuple } from 'embercom/services/content-suggestions-service';

interface Args {
  activeContent: LocalizedKnowledgeContent & Model;
  onClose?: () => Promise<void>;
  showBackButton?: boolean;
  showDrawerControls: boolean;
}

interface Signature {
  Args: Args;
  Element: HTMLDivElement;
}

export default class ContentReview extends Component<Signature> {
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare permissionsService: $TSFixMe;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare router: RouterService;
  @service declare appService: $TSFixMe;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare suggestionService: SuggestionService;
  @service declare store: Store;
  @service declare contentImportService: ContentImportService;
  @service declare navbarCollapsingService: $TSFixMe;

  @trackedInLocalStorage({
    keyName: DRAWER_EDITOR_SHOW_SIDE_PANEL,
  })
  showSidePanel = true;
  @tracked updatedSuggestionBlocks: Array<Block> = [];
  @tracked declare activeContent: ReviewableContent;

  constructor(owner: any, args: Args) {
    super(owner, args);

    this.showSidePanel = storage.get(DRAWER_EDITOR_SHOW_SIDE_PANEL) ?? true;

    this.expandToFullScreenIfNeeded();

    this.activeContent = this.args.activeContent as ReviewableContent;
    this.suggestionService.hasDirtyContent = false;
  }

  get showRightSlot() {
    return (
      this.sidePanelVisible &&
      this.isOnSuggestionsPage &&
      this.appService.app.canUseAiInsightsSuggestionsUi
    );
  }

  get sidePanelVisible(): boolean {
    return this.showSidePanel && !this.isReplacingSuggestions;
  }

  get editorConfig(): KnowledgeHubEditorConfig | undefined {
    return this.args.activeContent && this.args.activeContent.editorConfig
      ? this.args.activeContent.editorConfig
      : undefined;
  }

  get reviewRequests() {
    return this.activeContent.contentReviewRequests;
  }

  get isSavingContent() {
    return (
      taskFor(this.suggestionService.performSaveAction).isRunning ||
      taskFor(this.suggestionService.performSaveSnippetContent).isRunning
    );
  }

  get showCancelButton() {
    return !this.isOnSuggestionsPage && !this.isOnAiInsightsPage;
  }

  get isOnAiInsightsPage() {
    return (
      RouteRegexes.automation.analyze.test(this.router.currentRouteName) ||
      RouteRegexes.standalone.analyze.test(this.router.currentRouteName)
    );
  }

  get isOnSuggestionsPage() {
    return (
      RouteRegexes.automation.contentSuggestions.test(this.router.currentRouteName) ||
      RouteRegexes.standalone.contentSuggestions.test(this.router.currentRouteName)
    );
  }

  get isReplacingSuggestions(): boolean {
    return taskFor(this.suggestionService.replaceReviewRequest).isRunning;
  }

  get isPublishing() {
    return taskFor(this.suggestionService.publishArticleContentTask).isRunning;
  }

  get isSavingAsDraft() {
    return taskFor(this.suggestionService.saveArticleAsDraftTask).isRunning;
  }

  get isAllRejected() {
    return this.reviewRequests.every(
      (request: ContentReviewRequest) => request.get('status') === Status.Rejected,
    );
  }

  get allReviewRequestsAreActioned() {
    return this.reviewRequests.every((request) => request.get('status') !== Status.Pending);
  }

  @action
  stopReplacingSuggestions() {
    taskFor(this.suggestionService.replaceReviewRequest).cancelAll();
    history.back();
  }

  @action
  toggleSidePanel(): void {
    this.showSidePanel = !this.showSidePanel;

    this.expandToFullScreenIfNeeded();

    this.knowledgeHubEditorService.trackAnalyticsEvent(
      `${this.showSidePanel ? 'show' : 'hide'}_details_side_panel`,
    );
  }

  @action
  async onRejectSuggestion() {
    if (this.isAllRejected && !this.suggestionService.hasDirtyContent) {
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.content-editor.review.notification.rejected-all.success'),
      );
      await this.onAllReviewRequestsActioned();
    }
  }

  @action
  async saveArticleAsDraft() {
    await taskFor(this.suggestionService.saveArticleAsDraftTask).perform();
    this.notificationsService.notifyConfirmation(
      this.intl.t('knowledge-hub.content-editor.review.notification.saved.success'),
    );

    if (this.allReviewRequestsAreActioned) {
      await this.onAllReviewRequestsActioned();
    } else {
      await this.suggestionService.reloadContent({ isSaveAsDraft: true });
    }
  }

  @action
  async publishArticleContent() {
    await taskFor(this.suggestionService.publishArticleContentTask).perform();
    this.notificationsService.notifyConfirmation(
      this.intl.t('knowledge-hub.content-editor.review.notification.published.success'),
    );

    if (this.allReviewRequestsAreActioned) {
      await this.onAllReviewRequestsActioned();
    }
  }

  @action
  async saveContentSnippet() {
    await taskFor(this.suggestionService.saveSnippetTask).perform();
    this.notificationsService.notifyConfirmation(
      this.intl.t('knowledge-hub.content-editor.review.notification.saved.success'),
    );

    if (this.allReviewRequestsAreActioned) {
      await this.onAllReviewRequestsActioned();
    }
  }

  @action
  async onNewContentStatusChange(newStatus: Status, contentToLoad?: EntityIdentifierTuple) {
    let notificationMessage = this.newContentNotificationMessage(newStatus);

    this.notificationsService.notifyConfirmation(notificationMessage);

    if (this.allReviewRequestsAreActioned) {
      await this.onAllReviewRequestsActioned(contentToLoad);
    }
  }

  @action
  updateSuggestionBlocks(blocks: Array<Block>) {
    this.updatedSuggestionBlocks = blocks;
  }

  @action
  async onClose() {
    await this.closeContentDrawer();
  }

  @action
  onToggleFullScreen() {
    if (!this.knowledgeHubDrawerEditorService.isFullScreen) {
      this.showSidePanel = false;
    }
  }

  async onAllReviewRequestsActioned(contentToLoad?: EntityIdentifierTuple) {
    if (!this.isOnAiInsightsPage && !this.isOnSuggestionsPage) {
      return this.openContentInViewMode();
    }

    this.contentSuggestionsService.markSuggestionAsCompleted({
      entityId: Number(this.activeContent.entityId),
      entityType: this.activeContent.entityType,
    });

    let nextContent =
      contentToLoad ??
      this.contentSuggestionsService.getNextSuggestionFromList({
        entityId: Number(this.activeContent.entityId),
        entityType: this.activeContent.entityType,
      });

    if (nextContent) {
      this.openContentInReviewMode(nextContent);
    } else {
      // Close the drawer editor if no other suggestion exists. In focus mode this
      // will reset the drawer editor query params which will show the empty state instead
      await this.closeContentDrawer();
    }

    //remove the suggestion from the list and refresh the summaries after 1 second
    await timeout(ENV.APP._1000MS);

    this.contentSuggestionsService.removeSuggestionFromList({
      entity_id: Number(this.activeContent.entityId),
      entity_type: this.activeContent.entityType,
    });

    await this.contentSuggestionsService.fetchTopicsSummary(false);
    await this.contentImportService.fetchContentReviewSummary(false);
  }

  private async closeContentDrawer() {
    await this.knowledgeHubDrawerEditorService.closeDrawer();
    await this.args.onClose?.(); // the above call does some transitioning so let's override it with the passed in onClose
  }

  private expandToFullScreenIfNeeded() {
    // If we are trying to show the side panel and the submenu is currently opened, then close the submenu
    if (
      !this.navbarCollapsingService.collapsed &&
      this.showSidePanel &&
      this.isOnSuggestionsPage &&
      this.appService.app.canUseAiInsightsSuggestionsUi
    ) {
      this.navbarCollapsingService.toggleNavbarCollapsed();
    }

    // If the side panel is open and the full screen is off, open in full screen
    if (this.showSidePanel && !this.knowledgeHubDrawerEditorService.isFullScreen) {
      this.knowledgeHubDrawerEditorService.toggleFullScreen();
    }

    // If the side panel is closed and the full screen is on, open in smaller screen
    if (!this.showSidePanel && this.knowledgeHubDrawerEditorService.isFullScreen) {
      this.knowledgeHubDrawerEditorService.toggleFullScreen();
    }
  }

  private openContentInViewMode() {
    this.knowledgeHubDrawerEditorService.openViewDrawer({
      activeContentId: this.activeContent.entityId,
      activeContentType: objectNames[this.activeContent.entityType],
    });
  }

  private openContentInReviewMode(content: EntityIdentifierTuple) {
    this.knowledgeHubDrawerEditorService.openReviewDrawer({
      activeContentId: content.entityId,
      activeContentType: objectNames[content.entityType],
    });
  }

  private newContentNotificationMessage(status: Status): string {
    let isUnderperforming = this.reviewRequests.firstObject?.isUnderperformingContent;
    let isApproved = status === Status.Approved;

    if (isUnderperforming) {
      return isApproved
        ? this.intl.t(
            'knowledge-hub.content-editor.review.notification.approved-underperforming-content.success',
          )
        : this.intl.t(
            'knowledge-hub.content-editor.review.notification.rejected-underperforming-content.success',
          );
    }

    return isApproved
      ? this.intl.t('knowledge-hub.content-editor.review.notification.added.success')
      : this.intl.t('knowledge-hub.content-editor.review.notification.rejected.content-success');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ContentReview': typeof ContentReview;
    'knowledge-hub/content-editor/content-review': typeof ContentReview;
  }
}
